import type { TestimonialWidgetData } from '@web/widgets/testimonial/type';
import type { TestimonialButtonSettings } from '@web/widgets/testimonial/type/testimonial-widget.type';
import type Swiper from 'swiper';
import type { SwiperOptions } from 'swiper';

import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { EButtonStyleType, ESize, ETestimonialLayout, ETestimonialType } from '@common/enums';
import { UserFacade } from '@web/store/user/user.facade';

import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { BLANK_TESTIMONIAL_AVATAR } from '@web/widgets/image/constants/image.consts';

import { getStyles } from './testimonial-view.component.styles';
import { SwiperModule } from 'swiper/angular';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { formatFileUrl, formatFileUrlWithResize } from '@components/sp-file-manager/src/helpers';
import type { SelectedFileExtended } from '@common/types/file.type';
import { SpDirectivesModule } from '@common/directives/directives.module';

@Component({
    selector: 'web-testimonial-view',
    templateUrl: './testimonial-view.component.html',
    styleUrls: ['./testimonial-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, SwiperModule, TranslateModule, SpDirectivesModule],
})
export class TestimonialViewComponent extends WebBaseViewComponent<TestimonialWidgetData> {
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly eduCdnUrl: string = '//1076228245.rsc.cdn77.org/';

    protected getStyles = getStyles;

    public instanceId = (id: number): number => id;
    public isEduTariffFree: boolean = this.userFacade.isEduTariffFree;
    public ETestimonialLayout = ETestimonialLayout;
    public ETestimonialType = ETestimonialType;
    public BLANK_TESTIMONIAL_AVATAR = BLANK_TESTIMONIAL_AVATAR;
    public MAX_MESSAGE_LENGTH: number = 100;
    public mainSwiper: Swiper;

    public swiperConfig: SwiperOptions = {
        navigation: {
            nextEl: '.swiper-button-next-unique',
            prevEl: '.swiper-button-prev-unique',
        },
    };

    public getButtonStyles(formButtonSettings: TestimonialButtonSettings): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[ESize.M]}`;

        if (formButtonSettings.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.buttonStyles}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[formButtonSettings.styleType]} ${this.classes?.buttonStyles}`;
    }

    public getClassLayoutType(data): string {
        switch (data.settings.type) {
            case ETestimonialType.avatarTop:
                return `${this.classes?.testimonial} ${this.classes?.avatarTopType}`;
            case ETestimonialType.avatarLeft:
                return `${this.classes?.testimonial} ${this.classes?.avatarLeftType}`;
            default:
                return `${this.classes?.testimonial} ${this.classes?.noAvatarType}`;
        }
    }

    public setSwiper(swiper: Swiper) {
        this.mainSwiper = swiper;

        if (this.widgetData.settings.isAutoScroll) {
            this.mainSwiper?.autoplay?.start();
        }
    }

    public get autoplay() {
        if (!this.widgetData.settings.isAutoScroll) {
            this.mainSwiper?.autoplay?.stop();
            return false;
        } else {
            this.mainSwiper?.autoplay?.start();
            return {
                delay: 3000,
            };
        }
    }

    public showFullTextGrid(ev: Event, id: string): void {
        const textEl = document.getElementById(id);
        textEl.style.webkitLineClamp = 'unset';
        this.hideElement(ev);
    }

    public showFullTextSlider(ev: Event, id: string): void {
        const textEl = document.getElementsByClassName(id);
        Array.from(textEl).forEach((el) => ((el as HTMLElement).style.webkitLineClamp = 'unset'));
        this.hideElement(ev);
    }

    public getSlidesPerView(totalItems: number, itemsInRow: number): number {
        return totalItems >= itemsInRow ? itemsInRow : totalItems;
    }

    public imgSrc(src: string): string {
        return formatFileUrlWithResize({ url: src } as SelectedFileExtended, this.eduCdnUrl, 720);
    }

    public imgFallbackSrc(src: string): string {
        return formatFileUrl({ url: src } as SelectedFileExtended, this.eduCdnUrl);
    }

    private hideElement(ev: Event): void {
        (ev.target as HTMLElement).style.display = 'none';
    }

    public get selectedCourseId() {
        return this.widgetData.selectedCourseId;
    }

    public get items() {
        return this.widgetData?.items;
    }

    public get isShowTestimonials(): boolean {
        return !!this.items.length && !this.isEduTariffFree;
    }
}
